import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Compass, ChevronRight, Building2, LogIn, Moon, Navigation, Loader2 } from 'lucide-react';
import MosqueMap from '../components/MosqueMap';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const mosquePath = (m) => `/region/${m.regency_slug}/${m.village_slug}/${m.slug}`;

const haversineKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [provinces, setProvinces] = useState([]);
  const [regencies, setRegencies] = useState([]);
  const [provinceId, setProvinceId] = useState('');
  const [regencyId, setRegencyId] = useState('');
  const [mosques, setMosques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userPos, setUserPos] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState('');
  const [platform, setPlatform] = useState(null);

  const findNearby = () => {
    setGeoError('');
    if (!navigator.geolocation) { setGeoError('Browser Anda tidak mendukung geolokasi.'); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setGeoLoading(false); },
      () => { setGeoLoading(false); setGeoError('Tidak dapat mengakses lokasi. Izinkan akses lokasi lalu coba lagi.'); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const sortedMosques = useMemo(() => {
    if (!userPos) return mosques;
    return [...mosques]
      .map((m) => ({
        ...m,
        _distanceKm: m.latitude != null && m.longitude != null ? haversineKm(userPos.lat, userPos.lng, m.latitude, m.longitude) : null,
      }))
      .sort((a, b) => (a._distanceKm == null ? 1 : 0) - (b._distanceKm == null ? 1 : 0) || (a._distanceKm || 0) - (b._distanceKm || 0));
  }, [mosques, userPos]);

  useEffect(() => {
    axios.get(`${API_URL}/api/platform-settings`).then((r) => setPlatform(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/regions`, { params: { level: 'province' } })
      .then((r) => {
        setProvinces(r.data);
        const kepri = r.data.find((p) => p.name === 'Kepulauan Riau');
        if (kepri) setProvinceId(kepri.id);
      }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!provinceId) { setRegencies([]); setRegencyId(''); return; }
    axios.get(`${API_URL}/api/regions`, { params: { level: 'regency', parent_id: provinceId } })
      .then((r) => setRegencies(r.data)).catch(() => {});
    setRegencyId('');
  }, [provinceId]);

  const fetchMosques = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (provinceId) params.province_id = provinceId;
      if (regencyId) params.regency_id = regencyId;
      const { data } = await axios.get(`${API_URL}/api/mosques`, { params });
      setMosques(data);
    } catch (e) {
      setMosques([]);
    } finally {
      setLoading(false);
    }
  }, [search, provinceId, regencyId]);

  useEffect(() => {
    const t = setTimeout(fetchMosques, 300);
    return () => clearTimeout(t);
  }, [fetchMosques]);

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="landing-page">
      {/* Hero */}
      <div className="relative overflow-hidden bg-[#022C22]">
        <div
          className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.pexels.com/photos/16619128/pexels-photo-16619128.jpeg')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/80 via-emerald-900/85 to-[#022C22]" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 pt-14 pb-24">
          <div className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-2 text-white">
              <div className="w-9 h-9 rounded-xl bg-amber-400/90 flex items-center justify-center">
                <Moon className="w-5 h-5 text-emerald-950" />
              </div>
              <span className="font-heading font-bold text-lg tracking-tight">Masjid Berdaya</span>
            </div>
            <button
              data-testid="landing-login-btn"
              onClick={() => navigate('/login')}
              className="flex items-center gap-2 text-white/90 hover:text-white text-sm font-body font-medium bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-2 rounded-full transition-colors"
            >
              <LogIn className="w-4 h-4" /> Masuk Takmir
            </button>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white tracking-tight mb-4 max-w-2xl"
          >
            {platform?.landing_title || 'Cari Masjid di Sekitarmu'}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-white/80 font-body text-base mb-8 max-w-xl"
          >
            {platform?.landing_subtitle || 'Platform digital masjid se-Kepulauan Riau. Temukan jadwal sholat, infaq, program, dan UMKM masjid dalam satu genggaman.'}
          </motion.p>

          {/* Search */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="backdrop-blur-md bg-white/95 rounded-3xl shadow-2xl p-4 border border-white/40"
          >
            <div className="relative mb-3">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <input
                data-testid="landing-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari nama masjid, kota, atau kecamatan..."
                className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-stone-50 border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div
                data-testid="landing-province-locked"
                className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-emerald-50 border border-emerald-200 font-body text-sm text-emerald-800 font-medium"
              >
                <MapPin className="w-4 h-4 flex-shrink-0" /> Kepulauan Riau
              </div>
              <select
                data-testid="landing-regency-select"
                value={regencyId}
                onChange={(e) => setRegencyId(e.target.value)}
                disabled={!provinceId}
                className="px-4 py-3 rounded-2xl bg-stone-50 border border-stone-200 focus:border-emerald-500 outline-none font-body text-sm text-stone-700 disabled:opacity-50"
              >
                <option value="">Semua Kabupaten/Kota</option>
                {regencies.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <button
              data-testid="find-nearby-btn"
              onClick={findNearby}
              disabled={geoLoading}
              className="mt-3 w-full flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white font-body font-semibold py-3 rounded-2xl transition-colors"
            >
              {geoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
              {userPos ? 'Perbarui: Masjid Terdekat' : 'Cari Masjid Terdekat'}
            </button>
            {geoError && <p data-testid="geo-error" className="text-xs text-red-600 font-body mt-2 text-center">{geoError}</p>}
          </motion.div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 -mt-12 relative z-20 pb-16">
        {/* Interactive Map */}
        <div className="mb-8">
          <MosqueMap mosques={mosques} userPos={userPos} />
        </div>

        {/* Results */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-heading font-semibold text-primary-dark">
            {loading ? 'Memuat...' : userPos ? `${mosques.length} Masjid · terdekat dahulu` : `${mosques.length} Masjid ditemukan`}
          </h2>
        </div>

        {!loading && mosques.length === 0 ? (
          <div className="bg-white rounded-3xl border border-stone-100 p-12 text-center">
            <Building2 className="w-14 h-14 text-stone-300 mx-auto mb-4" />
            <p className="text-text-secondary font-body">Tidak ada masjid yang cocok dengan pencarian Anda</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="landing-mosque-list">
            {sortedMosques.map((m, i) => (
              <motion.button
                key={m.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.04 * i }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(mosquePath(m))}
                data-testid={`landing-mosque-${m.id}`}
                className="text-left bg-white rounded-2xl border border-stone-100 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all flex items-center gap-3 p-4"
              >
                <div
                  className="w-14 h-14 rounded-xl bg-emerald-50 bg-cover bg-center flex-shrink-0 flex items-center justify-center"
                  style={{ backgroundImage: m.logo_url ? `url('${m.logo_url}')` : 'none' }}
                >
                  {!m.logo_url && <Building2 className="w-7 h-7 text-emerald-300" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-body font-semibold text-text-primary truncate">{m.name}</h3>
                  <p className="text-xs font-body text-text-secondary flex items-center gap-1 truncate">
                    <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                    {m.village_name}, {m.regency_name}
                  </p>
                  {m._distanceKm != null && (
                    <span data-testid={`mosque-distance-${m.id}`} className="inline-flex items-center gap-1 text-[11px] font-body font-semibold text-emerald-700 mt-0.5">
                      <Navigation className="w-3 h-3" />
                      {m._distanceKm < 1 ? `${Math.round(m._distanceKm * 1000)} m` : `${m._distanceKm.toFixed(1)} km`} dari Anda
                    </span>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              </motion.button>
            ))}
          </div>
        )}
      </div>

      <footer className="border-t border-stone-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-8 text-center">
          <p className="text-sm font-body text-text-secondary mb-3">
            Punya masjid dan ingin bergabung? Daftarkan masjid Anda ke platform.
          </p>
          <button
            data-testid="landing-register-mosque-btn"
            onClick={() => navigate('/daftar-masjid')}
            className="text-emerald-700 hover:text-emerald-800 font-body font-semibold text-sm"
          >
            Daftarkan Masjid Anda →
          </button>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
